import { useEffect, useState } from 'react';
import type { RouteInfo } from '@bilmat/shared';
import { apiGet, ApiError } from '../lib/apiClient';
import { toQueryValue } from '../lib/queryValue';
import type { LocationInput } from '../state/store';

type UseRouteState = {
  route: RouteInfo | null;
  isLoading: boolean;
  error: string | null;
};

export function useRoute(origin: LocationInput, destination: LocationInput): UseRouteState {
  const [state, setState] = useState<UseRouteState>({
    route: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!origin.placeId || !destination.placeId) {
      setState({ route: null, isLoading: false, error: null });
      return;
    }

    const controller = new AbortController();
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    void apiGet<{ route: RouteInfo | null }>(
      'directions',
      {
        origin: toQueryValue(origin.placeId),
        destination: toQueryValue(destination.placeId),
      },
      controller.signal,
    )
      .then((data) => {
        setState({ route: data.route, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          err instanceof ApiError ? err.code : 'Kunde inte hämta rutt.';
        setState({ route: null, isLoading: false, error: message });
      });

    return () => controller.abort();
  }, [origin.placeId, destination.placeId]);

  return state;
}
